/**
 * CardMailShowcase
 *
 * A click-driven, 4-beat showcase of a real folded card on the /designs page:
 *   1. The closed card front.
 *   2. Tap: the front cover folds up and the full inside message is revealed in
 *      the handwriting style (CardInsidePreview).
 *   3. Tap: an addressed envelope drops down and covers the card (sample
 *      recipient + stamp).
 *   4. Tap: the card rises up out of the envelope, slides back inside, and the
 *      whole piece mails out.
 *
 * Layering is deliberately simple and OPAQUE: the envelope is one solid panel
 * that fully covers the card behind it, so the card never bleeds through. The
 * card sits BEHIND the envelope (z below) except while it is rising up out of
 * the mouth (z above). The 3D fold is kept under 90deg and faded so the design
 * never mirrors (the documented Chrome backface bug).
 *
 * Card renders at size="lg" (288x222, the true 6.5x5 folded ratio) and the whole
 * stage is scaled up so it fills the modal instead of floating small.
 */

import { useState, useEffect } from 'react';
import type { DesignData } from './CardDesignPreview';
import CardDesignPreview from './CardDesignPreview';
import CardInsidePreview from './CardInsidePreview';

const CARD_W = 288;
const CARD_H = 222;            // size="lg", true 1.3:1 folded ratio
const ENV_W  = 332;
const ENV_H  = 240;            // a touch taller than the card so it hides it fully
const STAGE_H = 380;
const ENV_TOP = STAGE_H - ENV_H - 24;            // envelope sits low in the stage
const CARD_TOP = ENV_TOP + (ENV_H - CARD_H) / 2; // card centered inside the envelope

// Polished sample content for the public /designs demo (shows the card full).
const SAMPLE_RECIPIENT = { name: 'Sarah Johnson', street: '742 Garden Lane', cityLine: 'Asheville, NC 28801' };
const SAMPLE_GREETING = 'Dear Sarah,';
const SAMPLE_MESSAGE =
  "Happy birthday! I hope this year is full of the people you love, a little " +
  "adventure, and plenty of reasons to smile. Here's to a wonderful year " +
  "ahead, and to celebrating you the way you deserve.";

type Insert = 'idle' | 'rising' | 'inside' | 'sealed' | 'shipped';

interface CardMailShowcaseProps {
  design:        DesignData;
  message:       string;
  cardGreeting?: string;
  cardClosing?:  string;
  cardFromLine?: string;
  cardFontId?:   number;
  onClose:       () => void;
}

export default function CardMailShowcase({
  design,
  message,
  cardGreeting,
  cardClosing,
  cardFromLine,
  cardFontId,
  onClose,
}: CardMailShowcaseProps) {
  const [step,     setStep]     = useState(0);
  const [insert,   setInsert]   = useState<Insert>('idle');
  const [showSent, setShowSent] = useState(false);

  // Scale the fixed-size stage down to fit narrow (mobile) screens.
  const [scale, setScale] = useState(1.3);
  useEffect(() => {
    const fit = () => {
      const maxW = Math.min(window.innerWidth - 40, 430);
      setScale(Math.max(0.72, Math.min(1.3, maxW / ENV_W)));
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);

  const accent  = design.accent_color ?? '#E8C12E';
  const opened  = step >= 1;
  const envIn   = step >= 2;
  const sealed  = insert === 'sealed' || insert === 'shipped';
  const shipped = insert === 'shipped';

  // The card is IN FRONT of the envelope only while it rises out of the mouth;
  // otherwise it is BEHIND the opaque envelope (so it can never show through).
  const cardInFront = step < 2 || insert === 'rising';

  // Use the real typed content when present, else polished sample content.
  const greeting = cardGreeting && !cardGreeting.includes('[') ? cardGreeting : SAMPLE_GREETING;
  const body     = message && message.trim() ? message : SAMPLE_MESSAGE;
  const closing  = cardClosing  || 'With love,';
  const fromLine = cardFromLine || 'Pete';

  const hint =
    step === 0 ? 'Tap to open the card' :
    step === 1 ? 'Tap to bring the envelope' :
    step === 2 ? 'Tap to mail it 📬' : '';

  function advance() {
    if (insert !== 'idle') return;
    if (step < 2) { setStep(step + 1); return; }
    setStep(3);
    setInsert('rising');
    setTimeout(() => setInsert('inside'),  780);   // slides back down into the envelope
    setTimeout(() => setInsert('sealed'),  1380);
    setTimeout(() => setInsert('shipped'), 1900);  // whole piece mails out
    setTimeout(() => setShowSent(true),    2100);
    setTimeout(onClose,                    3400);
  }

  // Card vertical offset from rest (inside the envelope).
  const cardY = insert === 'rising' ? -(CARD_H * 0.72) : 0;

  return (
    <div
      className="fixed inset-0 z-[70] flex flex-col items-center justify-center"
      style={{ background: 'rgba(15,25,50,0.88)', backdropFilter: 'blur(12px)' }}
      onClick={(e) => e.stopPropagation()}
    >
      <style>{`@keyframes csSent { from { opacity:0; transform:translateY(6px) } to { opacity:1; transform:translateY(0) } }`}</style>

      {/* Close */}
      <button onClick={onClose} aria-label="Close"
        className="absolute top-4 right-4 text-white/40 hover:text-white/80 transition-colors">
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Step dots (4) */}
      <div className="flex gap-2 mb-4">
        {[0, 1, 2, 3].map((s) => (
          <div key={s} style={{
            width: 7, height: 7, borderRadius: '50%',
            backgroundColor: step >= s ? '#fff' : 'rgba(255,255,255,0.25)',
            transition: 'background-color 0.3s',
          }} />
        ))}
      </div>

      {/* Scale the whole stage up so it fills the modal */}
      <div style={{ transform: `scale(${scale})`, transformOrigin: 'center' }}>
        <div onClick={advance}
          style={{ position: 'relative', width: ENV_W, height: STAGE_H,
                   cursor: insert === 'idle' ? 'pointer' : 'default' }}>
          {/* Whole mail piece lifts away on ship */}
          <div style={{
            position: 'absolute', inset: 0,
            transform: shipped ? 'translateY(-380px) rotate(-4deg)' : 'translateY(0)',
            opacity: shipped ? 0 : 1,
            transition: 'transform 0.85s cubic-bezier(0.4,0,1,1), opacity 0.85s ease',
          }}>

            {/* CARD — folds open, then rises out and slides back into the envelope */}
            <div style={{
              position: 'absolute', left: '50%', top: CARD_TOP,
              width: CARD_W, height: CARD_H,
              transform: `translateX(-50%) translateY(${cardY}px)`,
              transition: 'transform 0.7s cubic-bezier(0.22,1,0.36,1)',
              perspective: 1300,
              zIndex: cardInFront ? 6 : 1,
            }}>
              {/* Inside-right panel with the full message */}
              <div style={{ position: 'absolute', inset: 0, borderRadius: 10, overflow: 'hidden',
                            backgroundColor: '#FEFEFE', boxShadow: cardInFront ? '0 14px 40px rgba(0,0,0,0.35)' : 'none' }}>
                <CardInsidePreview
                  design={design}
                  message={body}
                  cardGreeting={greeting}
                  cardClosing={closing}
                  cardFromLine={fromLine}
                  cardFontId={cardFontId}
                  fontSize={11}
                />
              </div>
              {/* Front cover folds up to open */}
              <div style={{
                position: 'absolute', inset: 0,
                transformOrigin: 'center top',
                transform: opened ? 'rotateX(-84deg)' : 'rotateX(0deg)',
                opacity: opened ? 0 : 1,
                transition: 'transform 0.85s cubic-bezier(0.45,0,0.2,1), opacity 0.5s ease 0.35s',
                borderRadius: 10, overflow: 'hidden',
                boxShadow: opened ? 'none' : '0 18px 50px rgba(0,0,0,0.45)',
                zIndex: 2,
              }}>
                <CardDesignPreview design={design} size="lg" showName={false} />
              </div>
            </div>

            {/* ENVELOPE — one opaque panel that fully covers the card behind it */}
            <div style={{
              position: 'absolute', left: '50%', top: ENV_TOP, width: ENV_W, height: ENV_H,
              transform: `translateX(-50%) translateY(${envIn ? 0 : -340}px)`,
              opacity: envIn ? 1 : 0,
              transition: 'transform 0.6s cubic-bezier(0.22,1,0.36,1), opacity 0.4s ease',
              borderRadius: 10, background: 'linear-gradient(#FBF4E6,#EFE4D2)',
              boxShadow: '0 22px 55px rgba(0,0,0,0.45)', overflow: 'hidden',
              zIndex: 3,
            }}>
              {/* Flap (decorative V at the top); lifts slightly while the card moves */}
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: ENV_H * 0.5,
                background: 'linear-gradient(#F4ECDD,#E7DAC4)',
                clipPath: 'polygon(0 0, 100% 0, 50% 100%)',
                borderBottom: `1px solid ${accent}33`,
                transform: insert === 'rising' || insert === 'inside' ? 'translateY(-8px)' : 'translateY(0)',
                transition: 'transform 0.4s ease',
              }} aria-hidden />
              {/* Stamp */}
              <div style={{
                position: 'absolute', top: 16, right: 18, width: 38, height: 46, borderRadius: 3,
                background: '#fff', border: `1px dashed ${accent}`, display: 'flex',
                alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 5px rgba(0,0,0,0.15)',
              }}>
                <span style={{ fontSize: 20 }}>💌</span>
              </div>
              {/* Return + recipient address */}
              <p style={{ position: 'absolute', top: 12, left: 16, fontSize: 8, color: '#A79E90',
                          fontFamily: '"Inter",sans-serif', letterSpacing: '0.04em' }}>MomentSent LLC</p>
              <div style={{ position: 'absolute', left: 40, top: ENV_H * 0.54 }}>
                <p style={{ fontFamily: '"Inter",sans-serif', fontSize: 13, fontWeight: 700, color: '#1A1410', marginBottom: 3 }}>{SAMPLE_RECIPIENT.name}</p>
                <p style={{ fontFamily: '"Inter",sans-serif', fontSize: 12, color: '#3A332C', lineHeight: 1.45 }}>{SAMPLE_RECIPIENT.street}</p>
                <p style={{ fontFamily: '"Inter",sans-serif', fontSize: 12, color: '#3A332C', lineHeight: 1.45 }}>{SAMPLE_RECIPIENT.cityLine}</p>
              </div>
              {/* Seal dot when sealed */}
              <div style={{
                position: 'absolute', top: ENV_H * 0.46, left: '50%', transform: 'translateX(-50%)',
                width: 26, height: 26, borderRadius: '50%', background: accent,
                opacity: sealed ? 0.9 : 0, transition: 'opacity 0.3s ease',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13,
              }}>♥</div>
            </div>
          </div>
        </div>
      </div>

      {/* Mailbox as it ships */}
      <div style={{
        marginTop: 10, fontSize: 80, lineHeight: 1,
        opacity: shipped ? 1 : 0,
        transform: shipped ? 'translateY(0)' : 'translateY(16px)',
        transition: 'opacity 0.5s ease, transform 0.5s cubic-bezier(0.22,1,0.36,1)',
      }}>
        📬
      </div>

      {/* Hint / sent text */}
      <p style={{
        marginTop: 14,
        color: showSent ? '#FFFFFF' : 'rgba(255,255,255,0.55)',
        fontSize: showSent ? 18 : 13, fontWeight: showSent ? 700 : 400,
        fontFamily: '"Inter", sans-serif', letterSpacing: '0.03em',
        transition: 'color 0.3s, font-size 0.3s',
        ...(showSent ? { animation: 'csSent 0.4s ease both' } : {}),
      }}>
        {showSent ? 'On its way! 📬' : hint}
      </p>
    </div>
  );
}
